/**
 * RequestQueueManager - Manages concurrent request limits
 *
 * This singleton class ensures that only a specified number of requests
 * can be active at any given time. Additional requests are queued and
 * processed when slots become available.
 */

// Fallback when no valid concurrency cap is supplied. A queue with 0 (or NaN)
// concurrency never drains — processQueue() always early-returns, so every enqueued
// request hangs forever. This happens when MANUFACTURING_COST_CALCULATOR_MAX_CONCURRENT
// is unset: the view renders `ENV[...].to_i` => 0 => maxConcurrent 0.
const DEFAULT_MAX_CONCURRENT = 3;

class RequestQueueManager {
  constructor(maxConcurrent = parseInt(window.RequestQueueManagerC)) {
    const parsedMaxConcurrent = Number(maxConcurrent);
    this.maxConcurrent = parsedMaxConcurrent > 0 ? parsedMaxConcurrent : DEFAULT_MAX_CONCURRENT;
    this.activeRequests = 0;
    this.queue = [];
    this.completedRequests = 0;
    this.totalRequests = 0;
    this.progressCallbacks = [];
  }

  /**
   * Add a request to the queue
   * @param {Function} requestFn - Function that executes the request and returns a Promise
   * @returns {Promise} - Promise that resolves when the request completes
   */
  enqueue(requestFn) {
    this.totalRequests++;
    this.notifyProgress();

    return new Promise((resolve, reject) => {
      const queuedRequest = {
        requestFn,
        resolve,
        reject
      };

      this.queue.push(queuedRequest);
      this.processQueue();
    });
  }

  /**
   * Process the next request in the queue if capacity is available
   */
  processQueue() {
    // Check if we have capacity and pending requests
    if (this.activeRequests >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    // Get the next request from the queue
    const queuedRequest = this.queue.shift();
    this.activeRequests++;

    // Execute the request
    queuedRequest.requestFn()
      .then((result) => {
        this.activeRequests--;
        this.completedRequests++;
        this.notifyProgress();
        queuedRequest.resolve(result);
        this.processQueue(); // Process next request
      })
      .catch((error) => {
        this.activeRequests--;
        this.completedRequests++;
        this.notifyProgress();
        queuedRequest.reject(error);
        this.processQueue(); // Process next request even on error
      });
  }

  /**
   * Get current queue status (useful for debugging)
   */
  getStatus() {
    return {
      activeRequests: this.activeRequests,
      queuedRequests: this.queue.length,
      maxConcurrent: this.maxConcurrent,
      completedRequests: this.completedRequests,
      totalRequests: this.totalRequests
    };
  }

  /**
   * Register a callback to be notified of progress updates
   * @param {Function} callback - Function that receives progress info {completed, total, percentage}
   * @returns {Function} - Function to remove this callback
   */
  onProgress(callback) {
    this.progressCallbacks.push(callback);
    // Immediately notify with current progress
    this.notifyProgress();

    // Return unsubscribe function
    return () => this.removeProgressCallback(callback);
  }

  /**
   * Remove a progress callback
   * @param {Function} callback - The callback to remove
   */
  removeProgressCallback(callback) {
    this.progressCallbacks = this.progressCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Notify all progress callbacks with current progress
   */
  notifyProgress() {
    const rawPercentage = this.totalRequests > 0 ? Math.round((this.completedRequests / this.totalRequests) * 100) : 0;
    const progressInfo = {
      completed: this.completedRequests,
      total: this.totalRequests,
      percentage: Math.min(100, rawPercentage)  // Cap at 100% to handle race conditions with reset()
    };

    this.progressCallbacks.forEach(callback => {
      try {
        callback(progressInfo);
      } catch (error) {
        console.error('Error in progress callback:', error);
      }
    });
  }

  /**
   * Reset progress tracking
   */
  reset() {
    this.completedRequests = 0;
    this.totalRequests = 0;
    this.notifyProgress();
  }

  /**
   * Clear all pending requests and reset progress
   */
  clear() {
    this.queue = [];
    this.reset();
  }
}

// Export the class as default
export default RequestQueueManager;

// Export a default singleton instance for general use
export const defaultRequestQueueManager = new RequestQueueManager(  parseInt(window.RequestQueueManagerC) );
